// src/components/HomePage.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  useTheme,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  useToast,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Spinner,
  Link,
  Image,
  HStack,
} from "@chakra-ui/react";
import InfoTopicBox from "./InfoTopicBox";
import apiService from "../services/ApiService";
import { getCategoryImageUrl } from "../utils/fileUtils";

const HomePage = () => {
  const theme = useTheme();
  const toast = useToast();

  // NEW: topics state + loading
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);

  // Comment modal states (เดิม)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    VisitorName: "",
    VisitorTel: "",
    VisitorEmail: "",
    CommentText: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // NEW: fetch categories จาก API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiService({
          method: 'GET',
          url: '/categories?limit=50&is_active=true'
        });
        
        if (response.success) {
          // แปลงข้อมูลจาก API ให้ตรงกับรูปแบบที่ InfoTopicBox ต้องการ
          const categories = response.data.categories.map(category => ({
            id: category.id,
            title: category.title,
            description: category.description,
            image: getCategoryImageUrl(category.image_url) || '/topicimages/topic01.png', // fallback image
            link: category.link || `/topicdetail/${category.id}`
          }));
          setTopics(categories);
        } else {
          throw new Error(response.message || 'ไม่สามารถโหลดข้อมูลหมวดหมู่ได้');
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        toast({
          title: "โหลดข้อมูลหมวดหมู่ไม่สำเร็จ",
          description: err.message || "ไม่สามารถเชื่อมต่อกับ API ได้",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setTopics([]);
      } finally {
        setTopicsLoading(false);
      }
    };

    fetchCategories();
  }, [toast]);

  const handleComment = () => setIsModalOpen(true);
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };
  const validateForm = () => {
    const newErrors = {};
    if (!formData.VisitorName.trim()) newErrors.VisitorName = "กรุณากรอกชื่อ";
    if (!formData.CommentText.trim())
      newErrors.CommentText = "กรุณากรอกความคิดเห็น";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVisitorComments",
        data: {
          VisitorName: formData.VisitorName,
          VisitorTel: formData.VisitorTel,
          VisitorEmail: formData.VisitorEmail,
          CommentText: formData.CommentText,
        },
      });
      if (response.result === "success") {
        toast({
          title: "ส่งความคิดเห็นสำเร็จ",
          description: "ขอบคุณสำหรับความคิดเห็นของคุณ",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        setIsModalOpen(false);
        setFormData({
          VisitorName: "",
          VisitorTel: "",
          VisitorEmail: "",
          CommentText: "",
        });
      } else {
        setSubmitError("ไม่สามารถส่งความคิดเห็นได้");
        toast({
          title: "เกิดข้อผิดพลาด",
          description: "ไม่สามารถส่งความคิดเห็นได้",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      setSubmitError("เกิดข้อผิดพลาดในการส่งความคิดเห็น");
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "เกิดข้อผิดพลาดในการส่งความคิดเห็น",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const LinkToVaxhub = () => {
    return (
      <Box bg={theme.colors.gray[500]} borderRadius="full" boxShadow="2xl" m={15} pl={10} pr={10} pt={5} pb={5}>
      <Link
        href="https://vaxhub.dld.go.th/"
        target="_blank"
        style={{ textDecoration: "none" }}
      >
        <HStack
          spacing={4}
          align="center"
          justify="center"
          transition="transform 0.3s ease"
          _hover={{ transform: "scale(1.1)" }}
        >
          <Center
            w="80px"
            h="80px"
            bg="white"
            borderRadius="full"
            borderWidth={2}
            boxShadow="2xl"
            overflow="hidden"
          >
            <Image
              src={process.env.PUBLIC_URL + "/vaxhub.png" }
              alt="Vaxhub"
              objectFit="cover"
              w="100%"
              h="100%"
            />
          </Center>
          <Heading size="md" color="white" textAlign="center">
            ระบบสั่งจองวัคซีนออนไลน์
          </Heading>
        </HStack>
      </Link>
      </Box>
    );
  };

  return (
    <Box w="100%" bg="white" borderRadius="2xl" py={10}>
      <Center>
        <Box maxW="container.xl" w="100%" px={{ base: 4, md: 0 }}>
          <VStack spacing={8} align="center">
            <Box w="100%" h="150px" borderRadius="2xl" mb={5}>
              <Center>
                <VStack spacing={2}>
                  <Heading
                    textAlign="center"
                    color={theme.colors.teal[500]}
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                    fontWeight="normal"
                  >
                    รายงานผลการดำเนินงานเงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่ายประจำปี
                    ที่สำนักงานเทคโนโลยีชีวภัณฑ์สัตว์จัดทำขึ้นมานี้
                    <br />
                    เพื่อสรุปผลการปฏิบัติงานด้านการผลิตวัคซีน
                    และการบริหารจัดการชีวภัณฑ์สัตว์ของเงินทุนหมุนเวียนเพื่อผลิตวัคซีนจำหน่าย
                    <br />
                    ใช้เป็นแนวทางในการพัฒนาและวิเคราะห์ผลการปฏิบัติงาน
                  </Heading>
                  <Box bg={theme.colors.teal[500]} w="80%" h="3px" mt={5} />
                </VStack>
              </Center>
            </Box>

            {topicsLoading ? (
              <Center w="100%" py={10}>
                <Spinner />
              </Center>
            ) : (
              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing={5}
                w="100%"
              >
                {topics.map((topic) => (
                  <InfoTopicBox
                    key={topic.id}
                    image={topic.image}
                    title={topic.title}
                    description={topic.description}
                    link={topic.link}
                  />
                ))}
              </SimpleGrid>
            )}

            <LinkToVaxhub />
          </VStack>
        </Box>
      </Center>

      {/* Modal ส่งความคิดเห็น (เหมือนเดิม) */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ส่งความคิดเห็น</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={errors.VisitorName} mb={4}>
              <FormLabel>ชื่อ *</FormLabel>
              <Input
                name="VisitorName"
                value={formData.VisitorName}
                onChange={handleInputChange}
                placeholder="กรอกชื่อ"
              />
              <FormErrorMessage>{errors.VisitorName}</FormErrorMessage>
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>หมายเลขโทรศัพท์</FormLabel>
              <Input
                name="VisitorTel"
                value={formData.VisitorTel}
                onChange={handleInputChange}
                placeholder="กรอกหมายเลขโทรศัพท์"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>อีเมล์</FormLabel>
              <Input
                name="VisitorEmail"
                value={formData.VisitorEmail}
                onChange={handleInputChange}
                placeholder="กรอกอีเมล์"
                type="email"
              />
            </FormControl>
            <FormControl isInvalid={errors.CommentText} mb={4}>
              <FormLabel>ความคิดเห็น *</FormLabel>
              <Textarea
                name="CommentText"
                value={formData.CommentText}
                onChange={handleInputChange}
                placeholder="กรอกความคิดเห็น"
                rows={5}
              />
              <FormErrorMessage>{errors.CommentText}</FormErrorMessage>
            </FormControl>
            {submitError && (
              <Text color="red.500" mb={4}>
                {submitError}
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleSubmit}
              isLoading={isSubmitting}
            >
              ส่ง
            </Button>
            <Button
              variant="ghost"
              onClick={() => setIsModalOpen(false)}
              isDisabled={isSubmitting}
            >
              ยกเลิก
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default HomePage;
